/**
 * Sources API Client
 *
 * API client for managing database sources (connections).
 */

import { httpClient } from "./client";
import type { IColumnProfile } from "@/shared/types";
import type {
  TConnectionType,
  TDataProviderType,
  IFilePattern,
  IEndpoint,
  ITopic,
} from "@/shared/types/source-explorer";
import {
  assertObject,
  assertEntityId,
  assertString,
  assertBoolean,
  assertNumber,
  assertOptionalString,
  assertOptionalNumber,
  assertOptionalBoolean,
  assertOptionalEnum,
  assertOptionalStringRecord,
  assertErrorParamsOrNull,
  assertEnum,
  assertArrayOf,
} from "../types/validation";

const SYNC_STATUS_VALUES = ['never', 'syncing', 'success', 'error'] as const;
const SSH_AUTH_METHOD_VALUES = ['password', 'private_key'] as const;
const CONNECTION_TYPE_VALUES = ['database', 'ftp', 'sftp', 's3', 'local_path', 'web_api', 'kafka'] as const;
const DATA_PROVIDER_TYPE_VALUES = ['database', 'excel', 'csv', 'json', 'xml'] as const;

export function validateSource(data: unknown, path: string): ISource {
  const obj = assertObject(data, path);
  return {
    id: assertEntityId(obj['id'], `${path}.id`),
    projectId: assertEntityId(obj['projectId'], `${path}.projectId`),
    groupId: assertEntityId(obj['groupId'], `${path}.groupId`),
    name: assertString(obj['name'], `${path}.name`),
    dbType: assertString(obj['dbType'], `${path}.dbType`),
    host: assertOptionalString(obj['host'], `${path}.host`),
    port: assertOptionalNumber(obj['port'], `${path}.port`),
    databaseName: assertOptionalString(obj['databaseName'], `${path}.databaseName`),
    username: assertOptionalString(obj['username'], `${path}.username`),
    isActive: assertBoolean(obj['isActive'], `${path}.isActive`),
    lastConnectedAt: assertOptionalString(obj['lastConnectedAt'], `${path}.lastConnectedAt`),
    lastSyncedAt: assertOptionalString(obj['lastSyncedAt'], `${path}.lastSyncedAt`),
    syncStatus: assertEnum(obj['syncStatus'], SYNC_STATUS_VALUES, `${path}.syncStatus`),
    syncError: assertOptionalString(obj['syncError'], `${path}.syncError`),
    createdAt: assertString(obj['createdAt'], `${path}.createdAt`),
    updatedAt: assertString(obj['updatedAt'], `${path}.updatedAt`),
    sshEnabled: assertOptionalBoolean(obj['sshEnabled'], `${path}.sshEnabled`),
    sshHost: assertOptionalString(obj['sshHost'], `${path}.sshHost`),
    sshPort: assertOptionalNumber(obj['sshPort'], `${path}.sshPort`),
    sshUsername: assertOptionalString(obj['sshUsername'], `${path}.sshUsername`),
    sshAuthMethod: assertOptionalEnum(obj['sshAuthMethod'], SSH_AUTH_METHOD_VALUES, `${path}.sshAuthMethod`),
    sshPrivateKeyPath: assertOptionalString(obj['sshPrivateKeyPath'], `${path}.sshPrivateKeyPath`),
    sshTimeoutSeconds: assertOptionalNumber(obj['sshTimeoutSeconds'], `${path}.sshTimeoutSeconds`),
    sshKeepaliveEnabled: assertOptionalBoolean(obj['sshKeepaliveEnabled'], `${path}.sshKeepaliveEnabled`),
    sshKeepaliveIntervalSeconds: assertOptionalNumber(obj['sshKeepaliveIntervalSeconds'], `${path}.sshKeepaliveIntervalSeconds`),
    odbcDriver: assertOptionalString(obj['odbcDriver'], `${path}.odbcDriver`),
    odbcTemplate: assertOptionalString(obj['odbcTemplate'], `${path}.odbcTemplate`),
    odbcFields: assertOptionalStringRecord(obj['odbcFields'], `${path}.odbcFields`),
    connectionType: assertOptionalEnum(obj['connectionType'], CONNECTION_TYPE_VALUES, `${path}.connectionType`),
    dataProviderType: assertOptionalEnum(obj['dataProviderType'], DATA_PROVIDER_TYPE_VALUES, `${path}.dataProviderType`),
  };
}

export function validateTestConnectionResponse(data: unknown, path: string): ITestConnectionResponse {
  const obj = assertObject(data, path);
  return {
    success: assertBoolean(obj['success'], `${path}.success`),
    error: assertOptionalString(obj['error'], `${path}.error`),
    errorCode: assertOptionalString(obj['errorCode'], `${path}.errorCode`),
  };
}

export function validateSyncEnqueueResponse(data: unknown, path: string): ISyncEnqueueResponse {
  const obj = assertObject(data, path);
  return {
    queued: assertBoolean(obj['queued'], `${path}.queued`),
    queueId: assertNumber(obj['queueId'], `${path}.queueId`),
  };
}

export function validateCachedSchema(data: unknown, path: string): ICachedSchema {
  const obj = assertObject(data, path);
  return {
    id: assertEntityId(obj['id'], `${path}.id`),
    name: assertString(obj['name'], `${path}.name`),
    tableCount: assertNumber(obj['tableCount'], `${path}.tableCount`),
  };
}

const TABLE_TYPE_VALUES = ['table', 'view', 'materialized_view'] as const;

export function validateCachedTable(data: unknown, path: string): ICachedTable {
  const obj = assertObject(data, path);
  return {
    id: assertEntityId(obj['id'], `${path}.id`),
    name: assertString(obj['name'], `${path}.name`),
    dbComment: assertOptionalString(obj['dbComment'], `${path}.dbComment`),
    rowCountEstimate: assertOptionalNumber(obj['rowCountEstimate'], `${path}.rowCountEstimate`),
    sizeBytes: assertOptionalNumber(obj['sizeBytes'], `${path}.sizeBytes`),
    hasPrimaryKey: assertBoolean(obj['hasPrimaryKey'], `${path}.hasPrimaryKey`),
    columnCount: assertNumber(obj['columnCount'], `${path}.columnCount`),
    tableType: assertOptionalEnum(obj['tableType'], TABLE_TYPE_VALUES, `${path}.tableType`) ?? 'table',
  };
}

export function validateCachedColumn(data: unknown, path: string): ICachedColumn {
  const obj = assertObject(data, path);
  return {
    id: assertEntityId(obj['id'], `${path}.id`),
    name: assertString(obj['name'], `${path}.name`),
    dataType: assertString(obj['dataType'], `${path}.dataType`),
    isNullable: assertBoolean(obj['isNullable'], `${path}.isNullable`),
    isPrimaryKey: assertBoolean(obj['isPrimaryKey'], `${path}.isPrimaryKey`),
    isForeignKey: assertBoolean(obj['isForeignKey'], `${path}.isForeignKey`),
    dbComment: assertOptionalString(obj['dbComment'], `${path}.dbComment`),
    analystNote: assertOptionalString(obj['analystNote'], `${path}.analystNote`),
  };
}

export function validateCachedColumnConstraint(data: unknown, path: string): ICachedColumnConstraint {
  const obj = assertObject(data, path);
  return {
    columnName: assertString(obj['columnName'], `${path}.columnName`),
    constraintType: assertString(obj['constraintType'], `${path}.constraintType`),
    constraintName: assertString(obj['constraintName'], `${path}.constraintName`),
    referencedTable: assertOptionalString(obj['referencedTable'], `${path}.referencedTable`),
    referencedColumn: assertOptionalString(obj['referencedColumn'], `${path}.referencedColumn`),
    checkClause: assertOptionalString(obj['checkClause'], `${path}.checkClause`),
  };
}

export function validateIncomingRelation(data: unknown, path: string): IIncomingRelation {
  const obj = assertObject(data, path);
  return {
    sourceTableId: assertEntityId(obj['sourceTableId'], `${path}.sourceTableId`),
    sourceTableName: assertString(obj['sourceTableName'], `${path}.sourceTableName`),
    sourceSchemaName: assertString(obj['sourceSchemaName'], `${path}.sourceSchemaName`),
    sourceSchemaId: assertEntityId(obj['sourceSchemaId'], `${path}.sourceSchemaId`),
    sourceColumnName: assertString(obj['sourceColumnName'], `${path}.sourceColumnName`),
    constraintName: assertString(obj['constraintName'], `${path}.constraintName`),
    referencedColumn: assertOptionalString(obj['referencedColumn'], `${path}.referencedColumn`),
  };
}

function assertStringArray(value: unknown, path: string): readonly string[] {
  return assertArrayOf(value, assertString, path);
}

export function validateCachedIndex(data: unknown, path: string): ICachedIndex {
  const obj = assertObject(data, path);
  return {
    indexName: assertString(obj['indexName'], `${path}.indexName`),
    indexType: assertString(obj['indexType'], `${path}.indexType`),
    columns: assertStringArray(obj['columns'], `${path}.columns`),
    isUnique: assertBoolean(obj['isUnique'], `${path}.isUnique`),
    isPrimary: assertBoolean(obj['isPrimary'], `${path}.isPrimary`),
    predicate: assertOptionalString(obj['predicate'], `${path}.predicate`),
    definition: assertOptionalString(obj['definition'], `${path}.definition`),
  };
}

const PROFILE_STATUS_VALUES = ['not_found', 'pending', 'processing', 'completed', 'failed'] as const;

export function validateColumnProfileStatus(data: unknown, path: string): IColumnProfileStatus {
  const obj = assertObject(data, path);
  const profileValue = obj['profile'];
  return {
    status: assertEnum(obj['status'], PROFILE_STATUS_VALUES, `${path}.status`),
    // profile is a complex nested type from shared/types — pass through if present
    profile: (profileValue === null || profileValue === undefined)
      ? (profileValue)
      : profileValue as IColumnProfile,
    error: assertOptionalString(obj['error'], `${path}.error`),
    // Машиночитаемый признак причины и параметры подстановки (api-errors-R06,
    // R07) — самостоятельные части отказа, а не текст. Норма:
    // features/source-explorer/profiling.md R08, R18.
    errorCode: assertOptionalString(obj['errorCode'], `${path}.errorCode`),
    errorParams: assertErrorParamsOrNull(obj['errorParams'] ?? null, `${path}.errorParams`),
  };
}

function validateEnqueueResponse(data: unknown, path: string): { success: boolean; message: string } {
  const obj = assertObject(data, path);
  return {
    success: assertBoolean(obj['success'], `${path}.success`),
    message: assertString(obj['message'], `${path}.message`),
  };
}

// ============================================================================
// Type Definitions
// ============================================================================

export interface ISource {
  readonly id: string;
  readonly projectId: string;
  readonly groupId: string;
  readonly name: string;
  readonly dbType: string;
  readonly host?: string;
  readonly port?: number;
  readonly databaseName?: string;
  readonly username?: string;
  readonly isActive: boolean;
  readonly lastConnectedAt?: string;
  readonly lastSyncedAt?: string;
  readonly syncStatus: "never" | "syncing" | "success" | "error";
  readonly syncError?: string;
  readonly createdAt: string;
  readonly updatedAt: string;
  readonly sshEnabled?: boolean;
  readonly sshHost?: string;
  readonly sshPort?: number;
  readonly sshUsername?: string;
  readonly sshAuthMethod?: 'password' | 'private_key';
  readonly sshPrivateKeyPath?: string;
  readonly sshTimeoutSeconds?: number;
  readonly sshKeepaliveEnabled?: boolean;
  readonly sshKeepaliveIntervalSeconds?: number;
  readonly odbcDriver?: string;
  readonly odbcTemplate?: string;
  readonly odbcFields?: Record<string, string>;
  readonly connectionType?: TConnectionType;
  readonly dataProviderType?: TDataProviderType;
}

interface ICreateSourceRequestSshFields {
  readonly sshEnabled?: boolean;
  readonly sshHost?: string;
  readonly sshPort?: number;
  readonly sshUsername?: string;
  readonly sshAuthMethod?: 'password' | 'private_key';
  readonly sshPassword?: string;
  readonly sshPrivateKeyPath?: string;
  readonly sshPassphrase?: string;
  readonly sshTimeoutSeconds?: number;
  readonly sshKeepaliveEnabled?: boolean;
  readonly sshKeepaliveIntervalSeconds?: number;
}

export interface ICreateHostBasedSourceRequest extends ICreateSourceRequestSshFields {
  readonly projectId: string;
  readonly groupId: string;
  readonly name: string;
  readonly dbType: string;
  readonly host: string;
  /**
   * Порт. Не отправляется для типов подключения без сетевого порта
   * (S3, Web API, Kafka, локальный путь) — норма
   * spec/features/data-sources/connection.md, data-sources-connection-R03.
   */
  readonly port?: number;
  readonly databaseName: string;
  readonly username: string;
  readonly password: string;
  readonly connectionType?: TConnectionType;
  readonly dataProviderType?: TDataProviderType;
  /**
   * Прочие параметры подключения — набор «код атрибута → значение» для тех
   * параметров типа подключения, под которые в контракте нет отдельного поля
   * (регион S3, базовый путь, параметры Web API и Kafka).
   *
   * Норма spec/features/data-sources/connection.md, data-sources-connection-R02:
   * все введённые пользователем параметры сохраняются и возвращаются при чтении.
   * Коды ключей — из каталога атрибутов подключения
   * (spec/schema/entities.md). Секреты сюда не кладутся (R17): они уходят
   * в поле пароля и хранятся зашифрованными.
   */
  readonly odbcFields?: Record<string, string>;
  /**
   * Секреты подключения — набор «код секрета каталога → значение».
   *
   * Норма spec/features/data-sources/connection.md, data-sources-connection-R17:
   * секретов у типа подключения может быть больше одного (у S3 — обе половины
   * пары учётных данных), и каждый хранится только зашифрованным и не
   * возвращается при чтении. Коды — из каталога атрибутов подключения
   * (spec/schema/entities.md): `s3_access_key`, `s3_secret_key`,
   * `ftp_password`, `sftp_password`, `sftp_private_key`, `webapi_auth_token`,
   * `kafka_sasl_password`. Пустое значение означает отсутствие секрета.
   */
  readonly secrets?: Record<string, string>;
}

export interface ICreateOdbcSourceRequest extends ICreateSourceRequestSshFields {
  readonly projectId: string;
  readonly groupId: string;
  readonly name: string;
  readonly dbType: 'odbc';
  readonly odbcDriver: string;
  readonly odbcTemplate: string;
  readonly odbcFields: Record<string, string>;
  readonly host?: string;
  readonly port?: number;
}

export type ICreateSourceRequest = ICreateHostBasedSourceRequest | ICreateOdbcSourceRequest;

interface ITestConnectionParamsSshFields {
  readonly sourceId?: string;
  readonly sshEnabled?: boolean;
  readonly sshHost?: string;
  readonly sshPort?: number;
  readonly sshUsername?: string;
  readonly sshAuthMethod?: 'password' | 'private_key';
  readonly sshPassword?: string;
  readonly sshPrivateKeyPath?: string;
  readonly sshPassphrase?: string;
  readonly sshTimeoutSeconds?: number;
}

export interface ITestHostBasedConnectionParamsRequest extends ITestConnectionParamsSshFields {
  readonly dbType: string;
  readonly host: string;
  readonly port: number;
  readonly databaseName: string;
  readonly username: string;
  readonly password: string;
}

export interface ITestOdbcConnectionParamsRequest extends ITestConnectionParamsSshFields {
  readonly dbType: 'odbc';
  readonly odbcDriver: string;
  readonly odbcTemplate: string;
  readonly odbcFields: Record<string, string>;
  readonly host?: string;
  readonly port?: number;
}

export type ITestConnectionParamsRequest =
  | ITestHostBasedConnectionParamsRequest
  | ITestOdbcConnectionParamsRequest;

export interface ITestConnectionResponse {
  readonly success: boolean;
  readonly error?: string;
  /**
   * Машиночитаемый признак причины, когда продукт её назвал: по нему интерфейс
   * собирает текст сам (api-errors-R06, R08). Смена ключа шифрования обязана
   * быть узнаваема — иначе пользователь не поймёт, что нужно ввести секрет
   * заново (invariants/encryption.md, R07).
   */
  readonly errorCode?: string;
}

export interface ITestSshConnectionRequest {
  readonly sshHost: string;
  readonly sshPort: number;
  readonly sshUsername: string;
  readonly sshAuthMethod: 'password' | 'private_key';
  readonly sshPassword?: string;
  readonly sshPrivateKeyPath?: string;
  readonly sshPassphrase?: string;
  readonly sshTimeoutSeconds: number;
  readonly sourceId?: string;
}

export interface ITestSshConnectionResponse {
  readonly success: boolean;
  readonly error?: string;
}

export interface ISyncEnqueueResponse {
  readonly queued: boolean;
  readonly queueId: number;
}

export interface ICachedSchema {
  readonly id: string;
  readonly name: string;
  readonly tableCount: number;
}

export type TApiTableType = 'table' | 'view' | 'materialized_view';

export interface ICachedTable {
  readonly id: string;
  readonly name: string;
  readonly dbComment?: string;
  readonly rowCountEstimate?: number;
  readonly sizeBytes?: number;
  readonly hasPrimaryKey: boolean;
  readonly columnCount: number;
  readonly tableType?: TApiTableType;
}

export interface ICachedColumn {
  readonly id: string;
  readonly name: string;
  readonly dataType: string;
  readonly isNullable: boolean;
  readonly isPrimaryKey: boolean;
  readonly isForeignKey: boolean;
  readonly dbComment?: string;
  readonly analystNote?: string;
}

export interface ICachedColumnConstraint {
  readonly columnName: string;
  readonly constraintType: string;
  readonly constraintName: string;
  readonly referencedTable?: string;
  readonly referencedColumn?: string;
  readonly checkClause?: string;
}

export interface IIncomingRelation {
  readonly sourceTableId: string;
  readonly sourceTableName: string;
  readonly sourceSchemaName: string;
  readonly sourceSchemaId: string;
  readonly sourceColumnName: string;
  readonly constraintName: string;
  readonly referencedColumn?: string;
}

export interface ICachedIndex {
  readonly indexName: string;
  readonly indexType: string;
  readonly columns: readonly string[];
  readonly isUnique: boolean;
  readonly isPrimary: boolean;
  readonly predicate?: string;
  readonly definition?: string;
}

export interface IColumnProfileStatus {
  readonly status:
    | "not_found"
    | "pending"
    | "processing"
    | "completed"
    | "failed";
  readonly profile?: IColumnProfile | null;
  /** Запасной текст (api-errors-R04) — используется, когда по errorCode показать нечего. */
  readonly error?: string | null;
  /**
   * Машиночитаемый признак причины отказа (api-errors-R06, R07). Отсутствует,
   * когда отказа нет. Норма: features/source-explorer/profiling.md R08, R18.
   */
  readonly errorCode?: string;
  /** Параметры подстановки для текста причины (api-errors-R06). */
  readonly errorParams?: Readonly<Record<string, string | number>> | null;
}

export interface ISourcesApiClient {
  listSources(): Promise<ISource[]>;
  getSource(sourceId: string): Promise<ISource>;
  createSource(request: ICreateSourceRequest): Promise<ISource>;
  updateSource(
    sourceId: string,
    request: ICreateSourceRequest
  ): Promise<ISource>;
  deleteSource(sourceId: string): Promise<void>;
  testConnection(sourceId: string): Promise<ITestConnectionResponse>;
  testConnectionParams(request: ITestConnectionParamsRequest): Promise<ITestConnectionResponse>;
  testSshConnection(request: ITestSshConnectionRequest): Promise<ITestSshConnectionResponse>;
  syncMetadata(sourceId: string): Promise<ISyncEnqueueResponse>;
  getCachedSchemas(sourceId: string): Promise<ICachedSchema[]>;
  getCachedTables(sourceId: string, schemaId: string): Promise<ICachedTable[]>;
  getCachedColumns(sourceId: string, tableId: string): Promise<ICachedColumn[]>;
  getCachedConstraints(
    sourceId: string,
    tableId: string
  ): Promise<ICachedColumnConstraint[]>;
  getIncomingRelations(
    sourceId: string,
    tableId: string
  ): Promise<IIncomingRelation[]>;
  getCachedIndexes(sourceId: string, tableId: string): Promise<ICachedIndex[]>;
  getColumnProfileStatus(
    sourceId: string,
    tableId: string,
    columnId: string
  ): Promise<IColumnProfileStatus>;
  enqueueColumnProfile(
    sourceId: string,
    tableId: string,
    columnId: string
  ): Promise<{ success: boolean; message: string }>;
  getMyPermissions(projectId: string): Promise<{ permissionsBitmask: number }>;
  moveSourceToGroup(sourceId: string, groupId: string): Promise<ISource>;

  // File patterns
  getFilePatterns(sourceId: string): Promise<IFilePattern[]>;
  getFilePattern(filePatternId: string): Promise<IFilePattern>;
  createFilePattern(sourceId: string, request: ICreateFilePatternRequest): Promise<IFilePattern>;
  updateFilePattern(filePatternId: string, request: IUpdateFilePatternRequest): Promise<IFilePattern>;
  deleteFilePattern(filePatternId: string): Promise<void>;

  // Endpoints
  getEndpoints(sourceId: string): Promise<IEndpoint[]>;
  getEndpoint(endpointId: string): Promise<IEndpoint>;
  createEndpoint(sourceId: string, request: ICreateEndpointRequest): Promise<IEndpoint>;
  updateEndpoint(endpointId: string, request: IUpdateEndpointRequest): Promise<IEndpoint>;
  deleteEndpoint(endpointId: string): Promise<void>;

  // Topics
  getTopics(sourceId: string): Promise<ITopic[]>;
  getTopic(topicId: string): Promise<ITopic>;
  createTopic(sourceId: string, request: ICreateTopicRequest): Promise<ITopic>;
  updateTopic(topicId: string, request: IUpdateTopicRequest): Promise<ITopic>;
  deleteTopic(topicId: string): Promise<void>;
}

// ============================================================================
// File Pattern Request DTOs
// ============================================================================

export interface ICreateFilePatternRequest {
  readonly name: string;
  readonly fileMask: string;
  readonly filePath: string;
  readonly dataProviderType: TDataProviderType;
  readonly providerConfig?: Record<string, string>;
}

export interface IUpdateFilePatternRequest {
  readonly name: string;
  readonly fileMask: string;
  readonly filePath: string;
  readonly dataProviderType: TDataProviderType;
  readonly providerConfig?: Record<string, string>;
}

// ============================================================================
// Endpoint Request DTOs
// ============================================================================

export interface ICreateEndpointRequest {
  readonly name: string;
  readonly method: string;
  readonly path: string;
  readonly dataProviderType: TDataProviderType;
}

export interface IUpdateEndpointRequest {
  readonly name: string;
  readonly method: string;
  readonly path: string;
  readonly dataProviderType: TDataProviderType;
}

// ============================================================================
// Topic Request DTOs
// ============================================================================

export interface ICreateTopicRequest {
  readonly name: string;
  readonly topicName: string;
  readonly dataProviderType: TDataProviderType;
}

export interface IUpdateTopicRequest {
  readonly name: string;
  readonly topicName: string;
  readonly dataProviderType: TDataProviderType;
}

// ============================================================================
// Runtime Validators for new types
// ============================================================================

export function validateFilePattern(data: unknown, path: string): IFilePattern {
  const obj = assertObject(data, path);
  return {
    id: String(obj['id']),
    sourceId: String(obj['sourceId']),
    name: assertString(obj['name'], `${path}.name`),
    fileMask: assertString(obj['fileMask'], `${path}.fileMask`),
    filePath: assertString(obj['filePath'], `${path}.filePath`),
    dataProviderType: assertEnum(obj['dataProviderType'], DATA_PROVIDER_TYPE_VALUES, `${path}.dataProviderType`),
    providerConfig: assertOptionalStringRecord(obj['providerConfig'], `${path}.providerConfig`) ?? {},
    createdAt: assertString(obj['createdAt'], `${path}.createdAt`),
    updatedAt: assertString(obj['updatedAt'], `${path}.updatedAt`),
  };
}

export function validateEndpoint(data: unknown, path: string): IEndpoint {
  const obj = assertObject(data, path);
  return {
    id: String(obj['id']),
    sourceId: String(obj['sourceId']),
    name: assertString(obj['name'], `${path}.name`),
    method: assertString(obj['method'], `${path}.method`),
    path: assertString(obj['path'], `${path}.path`),
    dataProviderType: assertEnum(obj['dataProviderType'], DATA_PROVIDER_TYPE_VALUES, `${path}.dataProviderType`),
    createdAt: assertString(obj['createdAt'], `${path}.createdAt`),
    updatedAt: assertString(obj['updatedAt'], `${path}.updatedAt`),
  };
}

export function validateTopic(data: unknown, path: string): ITopic {
  const obj = assertObject(data, path);
  return {
    id: String(obj['id']),
    sourceId: String(obj['sourceId']),
    name: assertString(obj['name'], `${path}.name`),
    topicName: assertString(obj['topicName'], `${path}.topicName`),
    dataProviderType: assertEnum(obj['dataProviderType'], DATA_PROVIDER_TYPE_VALUES, `${path}.dataProviderType`),
    createdAt: assertString(obj['createdAt'], `${path}.createdAt`),
    updatedAt: assertString(obj['updatedAt'], `${path}.updatedAt`),
  };
}

export class SourcesApiClient implements ISourcesApiClient {
  async listSources(): Promise<ISource[]> {
    const response = await httpClient.get<unknown>("/sources");
    return assertArrayOf(response.data, validateSource, 'listSources');
  }

  async getSource(sourceId: string): Promise<ISource> {
    const response = await httpClient.get<unknown>(`/sources/${sourceId}`);
    return validateSource(response.data, 'getSource');
  }

  /**
   * Отказ создания уходит вызывающему **как есть**: его разбирает общий
   * `toApiError` (spec/invariants/api-errors.md R06–R08). Собственной обёртки
   * зона источников не заводит — она прятала признак причины от общего
   * разбора, и до выбора текста по признаку дело не доходило.
   */
  async createSource(request: ICreateSourceRequest): Promise<ISource> {
    const response = await httpClient.post<unknown>("/sources", request);
    return validateSource(response.data, 'createSource');
  }

  async updateSource(
    sourceId: string,
    request: ICreateSourceRequest
  ): Promise<ISource> {
    const response = await httpClient.put<unknown>(
      `/sources/${sourceId}`,
      request
    );
    return validateSource(response.data, 'updateSource');
  }

  async deleteSource(sourceId: string): Promise<void> {
    await httpClient.delete(`/sources/${sourceId}`);
  }

  async testConnection(sourceId: string): Promise<ITestConnectionResponse> {
    const response = await httpClient.post<unknown>(
      `/sources/${sourceId}/test`
    );
    return validateTestConnectionResponse(response.data, 'testConnection');
  }

  async testConnectionParams(request: ITestConnectionParamsRequest): Promise<ITestConnectionResponse> {
    const response = await httpClient.post<unknown>('/sources/test-connection', request);
    return validateTestConnectionResponse(response.data, 'testConnectionParams');
  }

  async testSshConnection(request: ITestSshConnectionRequest): Promise<ITestSshConnectionResponse> {
    const response = await httpClient.post<unknown>(
      '/sources/test-ssh',
      request,
    );
    return validateTestConnectionResponse(response.data, 'testSshConnection');
  }

  async syncMetadata(sourceId: string): Promise<ISyncEnqueueResponse> {
    const response = await httpClient.post<unknown>(
      `/sources/${sourceId}/sync`
    );
    return validateSyncEnqueueResponse(response.data, 'syncMetadata');
  }

  async getCachedSchemas(sourceId: string): Promise<ICachedSchema[]> {
    const response = await httpClient.get<unknown>(
      `/sources/${sourceId}/metadata/schemas`
    );
    return assertArrayOf(response.data, validateCachedSchema, 'getCachedSchemas');
  }

  async getCachedTables(
    sourceId: string,
    schemaId: string
  ): Promise<ICachedTable[]> {
    const response = await httpClient.get<unknown>(
      `/sources/${sourceId}/metadata/tables`,
      { params: { schemaId } }
    );
    return assertArrayOf(response.data, validateCachedTable, 'getCachedTables');
  }

  async getCachedColumns(
    sourceId: string,
    tableId: string
  ): Promise<ICachedColumn[]> {
    const response = await httpClient.get<unknown>(
      `/sources/${sourceId}/metadata/columns`,
      { params: { tableId } }
    );
    return assertArrayOf(response.data, validateCachedColumn, 'getCachedColumns');
  }

  async getCachedConstraints(
    sourceId: string,
    tableId: string
  ): Promise<ICachedColumnConstraint[]> {
    const response = await httpClient.get<unknown>(
      `/sources/${sourceId}/metadata/constraints`,
      { params: { tableId } }
    );
    return assertArrayOf(response.data, validateCachedColumnConstraint, 'getCachedConstraints');
  }

  async getIncomingRelations(
    sourceId: string,
    tableId: string
  ): Promise<IIncomingRelation[]> {
    const response = await httpClient.get<unknown>(
      `/sources/${sourceId}/metadata/incoming-relations`,
      { params: { tableId } }
    );
    return assertArrayOf(response.data, validateIncomingRelation, 'getIncomingRelations');
  }

  async getCachedIndexes(
    sourceId: string,
    tableId: string
  ): Promise<ICachedIndex[]> {
    const response = await httpClient.get<unknown>(
      `/sources/${sourceId}/metadata/indexes`,
      { params: { tableId } }
    );
    return assertArrayOf(response.data, validateCachedIndex, 'getCachedIndexes');
  }

  async getColumnProfileStatus(
    sourceId: string,
    tableId: string,
    columnId: string
  ): Promise<IColumnProfileStatus> {
    const response = await httpClient.get<unknown>(
      `/sources/${sourceId}/tables/${encodeURIComponent(
        tableId
      )}/columns/${encodeURIComponent(columnId)}/profile`
    );
    return validateColumnProfileStatus(response.data, 'getColumnProfileStatus');
  }

  async enqueueColumnProfile(
    sourceId: string,
    tableId: string,
    columnId: string
  ): Promise<{ success: boolean; message: string }> {
    const response = await httpClient.post<unknown>(
      `/sources/${sourceId}/tables/${encodeURIComponent(
        tableId
      )}/columns/${encodeURIComponent(columnId)}/profile`
    );
    return validateEnqueueResponse(response.data, 'enqueueColumnProfile');
  }

  async moveSourceToGroup(sourceId: string, groupId: string): Promise<ISource> {
    const response = await httpClient.patch<unknown>(
      `/sources/${sourceId}/group`,
      { groupId },
    );
    return validateSource(response.data, 'moveSourceToGroup');
  }

  async getMyPermissions(projectId: string): Promise<{ permissionsBitmask: number }> {
    const response = await httpClient.get<unknown>(
      `/projects/${encodeURIComponent(projectId)}/my-permissions`
    );
    const obj = assertObject(response.data, 'getMyPermissions');
    return {
      permissionsBitmask: assertNumber(obj['permissionsBitmask'], 'getMyPermissions.permissionsBitmask'),
    };
  }

  // ===========================================================================
  // File Patterns CRUD
  // ===========================================================================

  async getFilePatterns(sourceId: string): Promise<IFilePattern[]> {
    const response = await httpClient.get<unknown>(`/sources/${sourceId}/file-patterns`);
    return assertArrayOf(response.data, validateFilePattern, 'getFilePatterns');
  }

  async getFilePattern(filePatternId: string): Promise<IFilePattern> {
    const response = await httpClient.get<unknown>(`/file-patterns/${filePatternId}`);
    return validateFilePattern(response.data, 'getFilePattern');
  }

  async createFilePattern(sourceId: string, request: ICreateFilePatternRequest): Promise<IFilePattern> {
    const response = await httpClient.post<unknown>(`/sources/${sourceId}/file-patterns`, request);
    return validateFilePattern(response.data, 'createFilePattern');
  }

  async updateFilePattern(filePatternId: string, request: IUpdateFilePatternRequest): Promise<IFilePattern> {
    const response = await httpClient.put<unknown>(`/file-patterns/${filePatternId}`, request);
    return validateFilePattern(response.data, 'updateFilePattern');
  }

  async deleteFilePattern(filePatternId: string): Promise<void> {
    await httpClient.delete(`/file-patterns/${filePatternId}`);
  }

  // ===========================================================================
  // Endpoints CRUD
  // ===========================================================================

  async getEndpoints(sourceId: string): Promise<IEndpoint[]> {
    const response = await httpClient.get<unknown>(`/sources/${sourceId}/endpoints`);
    return assertArrayOf(response.data, validateEndpoint, 'getEndpoints');
  }

  async getEndpoint(endpointId: string): Promise<IEndpoint> {
    const response = await httpClient.get<unknown>(`/endpoints/${endpointId}`);
    return validateEndpoint(response.data, 'getEndpoint');
  }

  async createEndpoint(sourceId: string, request: ICreateEndpointRequest): Promise<IEndpoint> {
    const response = await httpClient.post<unknown>(`/sources/${sourceId}/endpoints`, request);
    return validateEndpoint(response.data, 'createEndpoint');
  }

  async updateEndpoint(endpointId: string, request: IUpdateEndpointRequest): Promise<IEndpoint> {
    const response = await httpClient.put<unknown>(`/endpoints/${endpointId}`, request);
    return validateEndpoint(response.data, 'updateEndpoint');
  }

  async deleteEndpoint(endpointId: string): Promise<void> {
    await httpClient.delete(`/endpoints/${endpointId}`);
  }

  // ===========================================================================
  // Topics CRUD
  // ===========================================================================

  async getTopics(sourceId: string): Promise<ITopic[]> {
    const response = await httpClient.get<unknown>(`/sources/${sourceId}/topics`);
    return assertArrayOf(response.data, validateTopic, 'getTopics');
  }

  async getTopic(topicId: string): Promise<ITopic> {
    const response = await httpClient.get<unknown>(`/topics/${topicId}`);
    return validateTopic(response.data, 'getTopic');
  }

  async createTopic(sourceId: string, request: ICreateTopicRequest): Promise<ITopic> {
    const response = await httpClient.post<unknown>(`/sources/${sourceId}/topics`, request);
    return validateTopic(response.data, 'createTopic');
  }

  async updateTopic(topicId: string, request: IUpdateTopicRequest): Promise<ITopic> {
    const response = await httpClient.put<unknown>(`/topics/${topicId}`, request);
    return validateTopic(response.data, 'updateTopic');
  }

  async deleteTopic(topicId: string): Promise<void> {
    await httpClient.delete(`/topics/${topicId}`);
  }
}

// Singleton instance
let sourcesApiClient: SourcesApiClient | null = null;

export function getSourcesApiClient(): SourcesApiClient {
  sourcesApiClient ??= new SourcesApiClient();
  return sourcesApiClient;
}
